import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Plus, PencilSimple, Trash, FloppyDisk, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import ImageUpload, { resolveImageUrl } from "@/components/ImageUpload";
import { fmt } from "@/lib/format";

const blankItem = {
  category_id: "",
  name: "",
  description: "",
  image_url: "",
  base_price: 5.0,
  cost_price: 0.0,
  swiggy_price: "",
  zomato_price: "",
  swiggy_packaging: 0,
  zomato_packaging: 0,
  sizes: [
    { name: "Regular", price_delta: 0 },
    { name: "Large", price_delta: 1.0 },
  ],
  available: true,
};

export default function MenuManagement() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [editCat, setEditCat] = useState(null);

  const load = async () => {
    const [i, c] = await Promise.all([api.get("/menu-items"), api.get("/categories")]);
    setItems(i.data);
    setCategories(c.data);
  };

  useEffect(() => {
    load();
  }, []);

  const startNew = () => setEditing({ ...blankItem, category_id: categories[0]?.id || "" });

  const save = async () => {
    if (!editing.name || !editing.category_id || !editing.base_price) {
      toast.error("Name, category and price required");
      return;
    }
    // Normalize aggregator prices — empty string => null (fall back to base_price/markup)
    const norm = {
      ...editing,
      swiggy_price: editing.swiggy_price === "" || editing.swiggy_price == null ? null : Number(editing.swiggy_price),
      zomato_price: editing.zomato_price === "" || editing.zomato_price == null ? null : Number(editing.zomato_price),
      swiggy_packaging: Number(editing.swiggy_packaging) || 0,
      zomato_packaging: Number(editing.zomato_packaging) || 0,
    };
    try {
      if (norm.id) {
        const { id, ...payload } = norm;
        await api.patch(`/menu-items/${id}`, payload);
        toast.success("Updated");
      } else {
        await api.post("/menu-items", norm);
        toast.success("Created");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    await api.delete(`/menu-items/${id}`);
    load();
  };

  const saveCat = async () => {
    if (!editCat.name) return;
    try {
      if (editCat.id) {
        const { id, ...payload } = editCat;
        await api.patch(`/categories/${id}`, payload);
      } else {
        await api.post("/categories", editCat);
      }
      setEditCat(null);
      load();
    } catch (e) {
      toast.error("Save failed");
    }
  };
  const delCat = async (id) => {
    if (!window.confirm("Delete category? Items in it will remain but orphaned.")) return;
    await api.delete(`/categories/${id}`);
    load();
  };

  const catName = (id) => categories.find((c) => c.id === id)?.name || "—";

  return (
    <div className="h-screen flex flex-col">
      <div className="p-6 bg-white border-b border-boba-stoneLine flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold">Menu Management</h1>
          <p className="text-sm text-boba-inkSoft mt-1">Manage drinks, categories & pricing</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setEditCat({ name: "", emoji: "", sort_order: 0 })}
            className="h-11 px-4 border border-boba-stoneLine rounded-md bg-white font-medium text-sm hover:bg-stone-50"
            data-testid="add-category-btn"
          >
            + Category
          </button>
          <button
            onClick={startNew}
            className="h-11 px-4 bg-boba-matcha hover:bg-boba-matchaDark text-white rounded-md font-medium text-sm flex items-center gap-2"
            data-testid="add-item-btn"
          >
            <Plus size={18} weight="bold" /> Add Item
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto scroll-thin p-6 space-y-6">
        {/* Categories */}
        <div>
          <h2 className="font-display text-xl font-bold mb-3">Categories</h2>
          <div className="flex flex-wrap gap-2">
            {categories.map((c) => (
              <div key={c.id} className="bg-white border border-boba-stoneLine rounded-md px-3 py-2 flex items-center gap-2">
                <span>{c.emoji} {c.name}</span>
                <button onClick={() => setEditCat(c)} className="text-boba-inkSoft hover:text-boba-matcha"><PencilSimple size={14} /></button>
                <button onClick={() => delCat(c.id)} className="text-boba-inkSoft hover:text-boba-danger"><X size={14} /></button>
              </div>
            ))}
          </div>
        </div>

        {/* Items */}
        <div className="bg-white border border-boba-stoneLine rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-boba-muted text-xs uppercase tracking-widest text-boba-inkSoft font-mono">
              <tr>
                <th className="text-left px-4 py-3">Item</th>
                <th className="text-left px-4 py-3">Category</th>
                <th className="text-right px-4 py-3">Base Price</th>
                <th className="text-left px-4 py-3">Available</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="menu-items-table">
              {items.map((it) => (
                <tr key={it.id} className="border-t border-boba-stoneLine">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {it.image_url ? (
                        <img src={it.image_url} alt="" className="w-10 h-10 object-cover rounded" />
                      ) : (
                        <div className="w-10 h-10 bg-boba-muted rounded flex items-center justify-center text-xl">🥤</div>
                      )}
                      <div>
                        <div className="font-semibold">{it.name}</div>
                        <div className="text-xs text-boba-inkSoft">{it.description}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">{catName(it.category_id)}</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold">{fmt(it.base_price)}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-1 rounded-full ${it.available ? "bg-boba-matcha/10 text-boba-matcha" : "bg-stone-100 text-stone-500"}`}>
                      {it.available ? "Yes" : "No"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => setEditing(it)}
                      className="p-2 hover:bg-stone-100 rounded mr-1"
                      data-testid={`edit-item-${it.id}`}
                    >
                      <PencilSimple size={16} />
                    </button>
                    <button
                      onClick={() => del(it.id)}
                      className="p-2 hover:bg-red-50 text-boba-danger rounded"
                      data-testid={`delete-item-${it.id}`}
                    >
                      <Trash size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Item edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg" data-testid="item-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editing?.id ? "Edit Item" : "New Item"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <label className="text-xs uppercase tracking-widest text-boba-inkSoft font-mono">Name</label>
                <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="w-full mt-1 h-10 px-3 border border-boba-stoneLine rounded-md" data-testid="item-name" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-boba-inkSoft font-mono">Description</label>
                <input value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="w-full mt-1 h-10 px-3 border border-boba-stoneLine rounded-md" data-testid="item-description" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-widest text-boba-inkSoft font-mono">Category</label>
                  <select value={editing.category_id} onChange={(e) => setEditing({ ...editing, category_id: e.target.value })} className="w-full mt-1 h-10 px-2 border border-boba-stoneLine rounded-md bg-white" data-testid="item-category">
                    {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-boba-inkSoft font-mono">Base Price</label>
                  <input type="number" step="0.01" value={editing.base_price} onChange={(e) => setEditing({ ...editing, base_price: parseFloat(e.target.value) || 0 })} className="w-full mt-1 h-10 px-3 border border-boba-stoneLine rounded-md font-mono" data-testid="item-price" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-boba-inkSoft font-mono">Cost Price</label>
                  <input type="number" step="0.01" value={editing.cost_price ?? 0} onChange={(e) => setEditing({ ...editing, cost_price: parseFloat(e.target.value) || 0 })} className="w-full mt-1 h-10 px-3 border border-boba-stoneLine rounded-md font-mono" data-testid="item-cost-price" placeholder="For P&L / COGS" />
                </div>
              </div>

              {/* Aggregator pricing */}
              <div className="border border-boba-stoneLine rounded-md p-3 bg-boba-cream/40">
                <div className="text-xs uppercase tracking-widest text-boba-inkSoft font-mono mb-2 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[#FC8019]" /> Aggregator Pricing
                  <span className="text-[10px] text-boba-inkSoft normal-case tracking-normal font-sans">— leave blank to use Base × Markup% from Settings</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] uppercase tracking-widest text-boba-inkSoft font-mono flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#FC8019]" /> Swiggy Price
                    </label>
                    <input
                      type="number" step="0.01"
                      value={editing.swiggy_price ?? ""}
                      onChange={(e) => setEditing({ ...editing, swiggy_price: e.target.value })}
                      className="w-full mt-1 h-10 px-3 border border-boba-stoneLine rounded-md font-mono"
                      data-testid="item-swiggy-price"
                      placeholder="e.g. 30 (blank = auto)"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] uppercase tracking-widest text-boba-inkSoft font-mono flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#E23744]" /> Zomato Price
                    </label>
                    <input
                      type="number" step="0.01"
                      value={editing.zomato_price ?? ""}
                      onChange={(e) => setEditing({ ...editing, zomato_price: e.target.value })}
                      className="w-full mt-1 h-10 px-3 border border-boba-stoneLine rounded-md font-mono"
                      data-testid="item-zomato-price"
                      placeholder="e.g. 32 (blank = auto)"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] uppercase tracking-widest text-boba-inkSoft font-mono">Swiggy Packaging</label>
                    <input
                      type="number" step="0.01"
                      value={editing.swiggy_packaging ?? 0}
                      onChange={(e) => setEditing({ ...editing, swiggy_packaging: parseFloat(e.target.value) || 0 })}
                      className="w-full mt-1 h-10 px-3 border border-boba-stoneLine rounded-md font-mono"
                      data-testid="item-swiggy-packaging"
                      placeholder="e.g. 5"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] uppercase tracking-widest text-boba-inkSoft font-mono">Zomato Packaging</label>
                    <input
                      type="number" step="0.01"
                      value={editing.zomato_packaging ?? 0}
                      onChange={(e) => setEditing({ ...editing, zomato_packaging: parseFloat(e.target.value) || 0 })}
                      className="w-full mt-1 h-10 px-3 border border-boba-stoneLine rounded-md font-mono"
                      data-testid="item-zomato-packaging"
                      placeholder="e.g. 8"
                    />
                  </div>
                </div>
              </div>
              <div>
                <ImageUpload
                  label="Image"
                  value={editing.image_url}
                  onChange={(url) => setEditing({ ...editing, image_url: url })}
                  testId="item-image-upload"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-boba-inkSoft font-mono">Sizes</label>
                {editing.sizes.map((s, i) => (
                  <div key={i} className="flex gap-2 mt-1">
                    <input value={s.name} onChange={(e) => { const next = [...editing.sizes]; next[i] = { ...s, name: e.target.value }; setEditing({ ...editing, sizes: next }); }} className="flex-1 h-10 px-3 border border-boba-stoneLine rounded-md" />
                    <input type="number" step="0.01" value={s.price_delta} onChange={(e) => { const next = [...editing.sizes]; next[i] = { ...s, price_delta: parseFloat(e.target.value) || 0 }; setEditing({ ...editing, sizes: next }); }} className="w-24 h-10 px-3 border border-boba-stoneLine rounded-md font-mono" />
                    <button onClick={() => { const next = editing.sizes.filter((_, x) => x !== i); setEditing({ ...editing, sizes: next }); }} className="px-3 hover:bg-stone-100 rounded"><X size={16} /></button>
                  </div>
                ))}
                <button onClick={() => setEditing({ ...editing, sizes: [...editing.sizes, { name: "", price_delta: 0 }] })} className="mt-2 text-sm text-boba-matcha hover:underline">+ Add size</button>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={editing.available} onChange={(e) => setEditing({ ...editing, available: e.target.checked })} id="available" />
                <label htmlFor="available" className="text-sm">Available for sale</label>
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-boba-stoneLine">
                <button onClick={() => setEditing(null)} className="h-10 px-4 border border-boba-stoneLine rounded-md hover:bg-stone-50">Cancel</button>
                <button onClick={save} className="h-10 px-4 bg-boba-matcha hover:bg-boba-matchaDark text-white rounded-md font-medium flex items-center gap-2" data-testid="item-save">
                  <FloppyDisk size={16} weight="bold" /> Save
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Category dialog */}
      <Dialog open={!!editCat} onOpenChange={(o) => !o && setEditCat(null)}>
        <DialogContent className="max-w-md" data-testid="category-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editCat?.id ? "Edit Category" : "New Category"}</DialogTitle>
          </DialogHeader>
          {editCat && (
            <div className="space-y-3">
              <div>
                <label className="text-xs uppercase tracking-widest text-boba-inkSoft font-mono">Name</label>
                <input value={editCat.name} onChange={(e) => setEditCat({ ...editCat, name: e.target.value })} className="w-full mt-1 h-10 px-3 border border-boba-stoneLine rounded-md" data-testid="cat-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-widest text-boba-inkSoft font-mono">Emoji</label>
                  <input value={editCat.emoji} onChange={(e) => setEditCat({ ...editCat, emoji: e.target.value })} className="w-full mt-1 h-10 px-3 border border-boba-stoneLine rounded-md" data-testid="cat-emoji" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-boba-inkSoft font-mono">Sort</label>
                  <input type="number" value={editCat.sort_order || 0} onChange={(e) => setEditCat({ ...editCat, sort_order: parseInt(e.target.value) || 0 })} className="w-full mt-1 h-10 px-3 border border-boba-stoneLine rounded-md font-mono" />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-3 border-t border-boba-stoneLine">
                <button onClick={() => setEditCat(null)} className="h-10 px-4 border border-boba-stoneLine rounded-md hover:bg-stone-50">Cancel</button>
                <button onClick={saveCat} className="h-10 px-4 bg-boba-matcha hover:bg-boba-matchaDark text-white rounded-md font-medium" data-testid="cat-save">Save</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
